import { FileText, History } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { DocumentVersion } from "@/lib/documentVersion";

interface DocumentWatermarkProps {
  documentName: string;
  version: DocumentVersion | null;
  onUpdate?: (description: string) => void;
}

export const DocumentWatermark = ({ 
  documentName, 
  version,
  onUpdate 
}: DocumentWatermarkProps) => {
  const lastModified = version?.lastModified 
    ? new Date(version.lastModified).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : 'Nicht verfügbar';

  return (
    <Card className="min-w-0 p-4 bg-muted/30 border-dashed print:border-solid">
      <div className="flex min-w-0 flex-col items-start justify-between gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <FileText className="w-5 h-5 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">
              <span className="break-words">{documentName}</span>
            </p>
            <p className="text-xs text-muted-foreground">
              Version {version?.currentVersion || '1.0'} • Letzte Änderung: {lastModified}
            </p>
          </div>
        </div>
        
        <div className="flex max-w-full flex-wrap items-center gap-2">
          <Badge variant="outline" className="text-xs">
            DSGVO-konform
          </Badge>
          
          {version && version.changes.length > 0 && (
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="max-w-full gap-2">
                  <History className="w-4 h-4" />
                  Änderungsverlauf
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Änderungsverlauf: {documentName}</DialogTitle>
                  <DialogDescription>
                    Alle dokumentierten Änderungen und Versionen
                  </DialogDescription>
                </DialogHeader>
                
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-4">
                    {version.changes.map((change, index) => (
                      <Card key={change.id} className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant={index === 0 ? "default" : "secondary"}>
                              Version {change.version}
                            </Badge>
                            {index === 0 && (
                              <Badge variant="outline" className="text-xs">
                                Aktuell
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {new Date(change.date).toLocaleDateString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <p className="text-sm text-foreground mb-1">
                          {change.description}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          von {change.author}
                        </p>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
    </Card>
  );
};
